import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { brand, gray } from "../../../../theme/themePrimitives";
import { useCustomisation } from "../../../../context/CustomisationContext";

export type StatusFilterKey = "active" | "inactive";

export interface StatusFilterState {
  active: boolean;
  inactive: boolean;
}

interface StatusFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: StatusFilterState;
  onToggle: (key: StatusFilterKey) => void;
  onSave: () => void;
  clearFilters: () => void;
}

export const StatusFilterDrawer: React.FC<StatusFilterDrawerProps> = ({
  open,
  onClose,
  filters,
  onToggle,
  onSave,
  clearFilters,
}) => {
  const { customisations } = useCustomisation();
  const {
    GPC: { buttonsSettings },
  } = customisations.customisations;
  const [hoveredCheckbox, setHoveredCheckbox] =
    useState<StatusFilterKey | null>(null);
  const [hasChecked, setHasChecked] = useState<Boolean>(false);

  // Update hasChecked based on current filter state
  useEffect(() => {
    setHasChecked(filters.active || filters.inactive);
  }, [filters]);

  useEffect(() => {
    if (!open) {
      setHoveredCheckbox(null);
    }
  }, [open]);

  const checkboxItems: { key: StatusFilterKey; label: string }[] = [
    { key: "active", label: "Active" },
    { key: "inactive", label: "Inactive" },
  ];

  return (
    <Drawer
      id="user-attributes-status-filter-drawer"
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "white",
            boxSizing: "border-box",
            width: { xs: "100%", sm: 350 },
            p: 2,
          },
        },
      }}
    >
      <Stack height="100%" spacing={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Filter</Typography>
          <IconButton
            id="user-attributes-status-filter-close-button"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box flex={1} overflow="auto" pr={1}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Status</Typography>
          <Stack spacing={1}>
            {checkboxItems.map((item) => (
              <Box
                key={item.key}
                display="flex"
                alignItems="center"
                onMouseEnter={() => setHoveredCheckbox(item.key)}
                onMouseLeave={() => setHoveredCheckbox(null)}
                sx={{ cursor: "pointer" }}
                onClick={() => onToggle(item.key)}
              >
                <Checkbox
                  checked={filters[item.key]}
                  sx={{
                    ...(hoveredCheckbox === item.key && {
                      borderColor: gray[400],
                    }),
                  }}
                />
                <Typography>{item.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Stack
          pt={2}
          borderTop={`1px solid ${gray[300]}`}
          direction={"row"}
          spacing={1}
        >
          <Button
            onClick={onSave}
            sx={{
              borderRadius: "12px",
              backgroundColor: `${brand[600]} !important`,
              color: `white`,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: `${brand[700]} !important`,
              },
            }}
          >
            Save
          </Button>
          <Button
            onClick={clearFilters}
            sx={{
              borderRadius: "12px",
              backgroundColor: "white",
              border: `1px solid ${buttonsSettings?.gpcBtnPrimaryBgColor}`,
              color: `${buttonsSettings?.gpcBtnPrimaryBgColor}`,
              fontWeight: 600,
              "&.Mui-disabled": {
                border: "1px solid #4d4d4d42",
                color: "#999",
                backgroundColor: "#fff",
              },
            }}
            disabled={!hasChecked}
          >
            Clear all
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};
